import React from 'react';
import { trackFormSubmit } from '../utils/gtag';
import { Helmet } from 'react-helmet-async';
import { CheckCircle, ArrowRight } from 'lucide-react';

const WeightMindset: React.FC = () => {
  return (
    <main>
      <Helmet>
        <title>Reset Your Weight Mindset – A New Approach to Weight Loss</title>
        <meta name="description" content="Join the waitlist for Sharon's weight loss hypnotherapy course. Calmer, more compassionate change + a voucher for your first hypnosis audio." />
        <link rel="canonical" href="https://www.sharonclare-hypnotherapy.com/weight-mindset/" />
      </Helmet>

      {/* Hero */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom max-w-4xl mx-auto text-center">
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-gray-900 mb-4">Reset Your Weight Mindset – A New Approach to Weight Loss</h1>
          <p className="text-lg sm:text-xl text-gray-600 mb-2">What if weight loss didn’t have to be a battle? Discover a calmer, more compassionate way forward.</p>
          <p className="text-lg sm:text-xl text-gray-700">Join the waitlist for my upcoming weight loss hypnotherapy course and get a voucher towards your first <a href="/audios" className="text-accent-600 hover:text-accent-700 underline">hypnosis audio</a>. Also explore our <a href="/services" className="text-accent-600 hover:text-accent-700 underline">other services</a>.</p>
        </div>
      </section>

      {/* The struggle */}
      <section className="py-16 bg-white">
        <div className="container-custom max-w-4xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-heading font-bold text-gray-900 mb-6">The Struggle Isn’t Your Fault</h2>
          <p className="text-gray-700 leading-relaxed mb-4">You already know what to do. So why does it feel so hard?</p>
          <p className="text-gray-700 leading-relaxed mb-4">When your body is stuck in stress or low mood, it overrides your best intentions. Hunger hormones change. Cravings increase. And your subconscious mind starts running the show.</p>
          <p className="text-gray-700 leading-relaxed">This isn’t about willpower. It’s about biology — and that can be changed.</p>
        </div>
      </section>

      {/* How hypnotherapy helps */}
      <section className="py-16 bg-gradient-to-br from-accent-50 to-primary-50">
        <div className="container-custom max-w-5xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-heading font-bold text-gray-900 mb-6">How Hypnotherapy Helps</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
              <ul className="space-y-3 text-gray-700">
                {[
                  'Switch off the stress response',
                  'Reconnect with hunger and fullness cues',
                  'Calm emotional cravings',
                  'Boost confidence and motivation',
                  'Feel more in control — gently and naturally',
                ].map((item) => (
                  <li key={item} className="flex items-start space-x-3">
                    <CheckCircle className="w-5 h-5 text-green-500 mt-0.5" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <p className="text-gray-700 mt-4">When the body feels safe, change feels easier.</p>
            </div>
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
              <h3 className="font-heading font-semibold text-gray-900 mb-2">A Gentle First Step</h3>
              <p className="text-gray-700">As a thank you for signing up to the course waitlist, you’ll receive a voucher towards my Reset Your Weight Mindset hypnosis audio — your first gentle step toward calmer, more confident change.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Waitlist embed placeholder (Kit) */}
      <section className="py-16 bg-white">
        <div className="container-custom max-w-3xl mx-auto">
          <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100 text-center">
            <h3 className="text-2xl font-heading font-semibold text-gray-900 mb-4">Get My Voucher + Join the Waitlist</h3>
            <p className="text-gray-600 mb-6">Enter your email and I’ll send your audio voucher and add you to the course waitlist.</p>
            <form className="grid sm:grid-cols-3 gap-3 text-left" onSubmit={async (e) => {
              e.preventDefault();
              const form = e.currentTarget as HTMLFormElement;
              const formData = new FormData(form);
              const email = String(formData.get('email') || '').trim();
              const name = String(formData.get('name') || '').trim();
              if (!email) return;
              try {
                const res = await fetch('/api/subscribe', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ email, name, list: 'weight-waitlist', source: 'weight-mindset-page' })
                });
                const data = await res.json().catch(() => ({} as any));
                if (res.ok) {
                  trackFormSubmit('weight-waitlist', 'newsletter');
                  window.location.href = '/thank-you?from=weight-waitlist';
                } else {
                  const message = (data && data.error) ? data.error : 'Sorry, something went wrong. Please try again later.';
                  alert(message);
                }
              } catch (err) {
                alert('Network error. Please try again.');
              }
            }}>
              <input name="name" type="text" placeholder="Your name (optional)" className="sm:col-span-1 w-full rounded-lg border border-gray-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-accent-500" />
              <input name="email" type="email" required placeholder="Your email" className="sm:col-span-1 w-full rounded-lg border border-gray-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-accent-500" />
              <button type="submit" className="sm:col-span-1 btn-primary inline-flex items-center justify-center w-full">
                Join Waitlist
                <ArrowRight className="w-4 h-4 ml-2" />
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* Story */}
      <section className="py-16 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom max-w-3xl mx-auto">
          <h3 className="text-2xl font-heading font-bold text-gray-900 mb-6">The Locked Door</h3>
          <p className="text-gray-700 leading-relaxed mb-4">There was once a woman who stood in front of a locked door… She knew everything she needed was behind it: calm, balance, control. She’d read the books. She’d tried to push her way through. But the door wouldn’t open.</p>
          <p className="text-gray-700 leading-relaxed">Until one day… someone handed her a key. Small. Gentle. Just the right fit. And everything changed — not through force, but because she finally stopped trying to do it all alone.</p>
        </div>
      </section>
    </main>
  );
};

export default WeightMindset;


